/**
 * Datadog Implementation (web)
 *
 */
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import Constants from 'expo-constants';
import { useEffect, type ReactNode } from 'react';

import { getNativeAppClientInfo } from '@/modules/clientContext';

// Module-scoped so the guard survives component remounts (StrictMode, Fast Refresh,
// conditional mounts). The browser SDK is itself a singleton, so a per-instance ref
// would let init() run again on remount and trigger the "loaded more than once" warning.
let isInitialized = false;

/**
 * DatadogProvider component for web
 * Initializes Datadog Browser SDK on mount
 */
export const DatadogProvider = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    if (isInitialized) {
      console.log('Datadog Web: Already initialized');
      return;
    }

    const environment =
      Constants.expoConfig?.extra?.environment || (__DEV__ ? 'development' : 'production');

    try {
      const isEnabled = process.env.EXPO_PUBLIC_DATADOG_ENABLED === 'true';
      if (!isEnabled && !__DEV__) {
        console.log('Datadog Web: Disabled via EXPO_PUBLIC_DATADOG_ENABLED flag');
        return;
      }

      const clientToken = process.env.EXPO_PUBLIC_DATADOG_CLIENT_TOKEN;
      const applicationId = process.env.EXPO_PUBLIC_DATADOG_APPLICATION_ID;

      if (!clientToken) {
        console.warn('Datadog Web: EXPO_PUBLIC_DATADOG_CLIENT_TOKEN is not set');
        return;
      }

      if (!applicationId) {
        console.warn('Datadog Web: EXPO_PUBLIC_DATADOG_APPLICATION_ID is not set');
        return;
      }

      const version = Constants.expoConfig?.version || '1.0.0';
      const sessionReplayEnabled =
        process.env.EXPO_PUBLIC_DATADOG_SESSION_REPLAY_ENABLED === 'true';

      const appName = Constants.expoConfig?.name || 'Onboarding';
      // Datadog `service` must be distinct from the main Vida app. `expoConfig.name`
      // is the product brand ("Vida"), which VidaReactNative also uses — deriving
      // `service` from it commingles both apps' RUM/logs under `service:Vida`.
      // "Onboarding" is the app-specific identifier; `application` keeps the brand
      // ("Vida") so the product grouping is preserved.
      const serviceName = 'Onboarding';

      datadogRum.init({
        applicationId,
        clientToken,
        site: 'datadoghq.com',
        service: serviceName,
        env: environment,
        version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: sessionReplayEnabled ? 100 : 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask',
        enableExperimentalFeatures: ['feature_flags'],
      });

      datadogLogs.init({
        clientToken,
        site: 'datadoghq.com',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
      });

      const vidaEnv = process.env.EXPO_PUBLIC_VIDA_ENV || environment;

      datadogRum.setGlobalContextProperty('service', serviceName);
      datadogRum.setGlobalContextProperty('application', appName);
      datadogRum.setGlobalContextProperty('environment', vidaEnv);
      datadogRum.setGlobalContextProperty('release', version);

      datadogLogs.setGlobalContextProperty('service', serviceName);
      datadogLogs.setGlobalContextProperty('application', appName);
      datadogLogs.setGlobalContextProperty('environment', vidaEnv);
      datadogLogs.setGlobalContextProperty('release', version);

      // Embed launch context (Sparta's setDatadogGlobalContext), stamped on every
      // RUM/Logs event. RUM values are stringified (Logs kept raw) to match the
      // shared convention in @vidahealth/utilos (datadog/setGlobalContext.ts) — also
      // mirrored by datadog.web.ts's setUserProperties — so Sparta-authored monitors
      // faceting on these keys match.
      const { vidaClient, vidaClientVersion, isLoadedInNativeApp } = getNativeAppClientInfo();
      // The boolean is ALWAYS set (false when not embedded) so dashboards can facet
      // on false vs. missing — matches Sparta's helper and our PostHog identify.
      datadogRum.setGlobalContextProperty(
        'is_view_embedded_in_native_app',
        `${isLoadedInNativeApp}`
      );
      datadogLogs.setGlobalContextProperty('is_view_embedded_in_native_app', isLoadedInNativeApp);
      // vida_client / vida_client_version are identifiers that only exist when
      // launched embedded — left absent (not `undefined`) otherwise.
      if (vidaClient) {
        datadogRum.setGlobalContextProperty('vida_client', vidaClient);
        datadogLogs.setGlobalContextProperty('vida_client', vidaClient);
        if (vidaClientVersion) {
          datadogRum.setGlobalContextProperty('vida_client_version', vidaClientVersion);
          datadogLogs.setGlobalContextProperty('vida_client_version', vidaClientVersion);
        }
      }

      if (sessionReplayEnabled) {
        datadogRum.startSessionReplayRecording();
      }

      isInitialized = true;
      console.log('Datadog Web: Successfully initialized');
    } catch (error) {
      console.error('Datadog Web: Failed to initialize', error);
    }
  }, []);

  return <>{children}</>;
};
